import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, Upload, Sparkles, Loader2, Share2, Download, RotateCcw, Lock, Globe, Coins, Trophy, Rocket } from "lucide-react";
import { magicStyles, magicGenerate, magicFileUrl, readAndDownscaleImage, magicToggleShare, pollMagicJob } from "@/lib/magicApi";
import BoostModal from "@/pages/magic/BoostModal";

const CAT_COLOR = { "funny": "#FFD700", "magical": "#B47BFF", "sci-fi": "#00F0FF",
  "cool": "#FF00E5", "art": "#FF7A00", "animals": "#39FF14" };

/**
 * MagicCreate — one-page flow:
 *  1) select a photo (upload or camera)
 *  2) preview + hit "Transform"
 *  3) result screen with before/after slider + share/save
 */
export default function MagicCreate() {
  const { style: styleSlug } = useParams();
  const [params] = useSearchParams();
  const isDaily = params.get("daily") === "1";
  const nav = useNavigate();
  const { status, refreshStatus } = useOutletContext() || {};

  const [style, setStyle] = useState(null);
  const [srcDataUrl, setSrcDataUrl] = useState("");
  const [srcBase64, setSrcBase64] = useState("");
  const [srcMime, setSrcMime] = useState("image/png");
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    magicStyles().then((r) => {
      const all = (r.categories || []).flatMap((c) => c.styles);
      const s = all.find((x) => x.slug === styleSlug);
      if (!s) { toast.error("Unknown style"); nav("/magic"); return; }
      setStyle(s);
    });
  }, [styleSlug, nav]);

  const onPick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Image too big (max 25 MB)"); return; }
    try {
      // Downscale on the client so Gemini never sees a 4000×3000 raw. This
      // is what killed transformations before — huge phone photos exceeded
      // the model's input-image limit and it returned 400 "Unable to
      // process input image". 1024 px is plenty since the model outputs
      // 1024 anyway; the smaller payload also uploads MUCH faster on 4G.
      const { base64, dataUrl, mime } = await readAndDownscaleImage(file, { maxSize: 1024, quality: 0.85 });
      setSrcDataUrl(dataUrl); setSrcBase64(base64); setSrcMime(mime);
      setResult(null); setError("");
    } catch (e) {
      toast.error("Couldn't read that photo — try another one.");
    }
  };

  const startOver = () => {
    setResult(null); setError(""); setSrcDataUrl(""); setSrcBase64("");
  };

  const transform = async () => {
    if (!srcBase64 || !style) return;
    setBusy(true); setError(""); setElapsed(0);
    try {
      const start = await magicGenerate({
        style_slug: style.slug,
        image_base64: `data:${srcMime};base64,${srcBase64}`,
        is_public: true,
      });
      // Poll the background job every 2s until done. Cloudflare can't time
      // us out — we return instantly and then poll.
      const done = await pollMagicJob(start.job_id, {
        interval: 2000,
        timeoutMs: 180000,
        onTick: (_r, secs) => setElapsed(secs),
      });
      const r = {
        generation: done.generation,
        coins_spent: done.coins_charged,
        was_free: done.was_free,
        bonus_coins: done.bonus_coins,
        coins: done.coins_left,
      };
      setResult(r);
      refreshStatus?.();
      if (r.bonus_coins > 0) toast.success(`+${r.bonus_coins} coins — Daily Challenge complete!`);
      else if (r.was_free) toast.success("Free generation used — enjoy!");
      else toast.success(`-${r.coins_spent} coins`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Something went wrong";
      setError(detail); toast.error(detail);
    } finally { setBusy(false); }
  };

  if (!style) return <div className="py-20 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const catColor = CAT_COLOR[style.category] || "#FF00E5";
  const gen = result?.generation;

  return (
    <div className="space-y-4" data-testid="magic-create">
      <div className="flex items-center gap-2">
        <Link to="/magic" data-testid="create-back" className="p-2 -ml-2 rounded-full hover:bg-white/5"><ArrowLeft className="w-4 h-4" /></Link>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: catColor }}>{style.category}</div>
          <div className="font-black text-2xl leading-none" data-testid="create-style-name">{style.name}</div>
        </div>
        {isDaily && (
          <span className="inline-flex items-center gap-1 bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" data-testid="daily-badge">
            <Trophy className="w-3 h-3" fill="currentColor" /> Daily · +50 coins
          </span>
        )}
      </div>

      {/* Pricing hint */}
      {status && !result && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 flex items-center gap-3" data-testid="create-price">
          <Sparkles className="w-4 h-4" style={{ color: catColor }} fill="currentColor" />
          {status.free_left > 0 ? (
            <div className="text-sm">You have <b>{status.free_left} free</b> generation{status.free_left === 1 ? "" : "s"} left today. This one is <b>on the house</b>.</div>
          ) : (
            <div className="text-sm">This transformation costs <b className="text-[#FFD700]">{status.coin_cost} coins</b>. You have {status.coins?.toLocaleString?.() || 0}. <Link to="/coins" className="underline">Get more</Link></div>
          )}
        </div>
      )}

      {/* Photo picker OR preview */}
      {!srcDataUrl && !result && (
        <PhotoPicker onPick={onPick} fileInputRef={fileInputRef} />
      )}

      {srcDataUrl && !result && (
        <section className="space-y-3" data-testid="create-preview">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black">
            <img src={srcDataUrl} alt="Selected" className="w-full max-h-[60vh] object-contain" data-testid="preview-img" />
            {busy && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="magic-loader">
                <MagicSpinner elapsed={elapsed} />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="create-transform-btn" onClick={transform} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#FF00E5] text-white font-black text-sm uppercase tracking-widest px-5 py-3 rounded-full disabled:opacity-40 shadow-[0_0_20px_rgba(255,0,229,0.4)]">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Working magic…</> : <><Sparkles className="w-4 h-4" fill="currentColor" /> Transform</>}
            </button>
            <button data-testid="create-change-btn" onClick={startOver} disabled={busy}
              className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-3 rounded-full disabled:opacity-40">
              <RotateCcw className="w-3.5 h-3.5" /> Change photo
            </button>
          </div>
          {error && <div className="text-sm text-[#F87171]" data-testid="create-error">{error}</div>}
        </section>
      )}

      {result && (
        <ResultView gen={gen} srcDataUrl={srcDataUrl} onStartOver={startOver}
          result={result} />
      )}
    </div>
  );
}

function PhotoPicker({ onPick, fileInputRef }) {
  const captureRef = useRef(null);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="photo-picker">
      <label data-testid="picker-upload"
        className="cursor-pointer rounded-3xl border-2 border-dashed border-[#7A00FF]/40 bg-[#150525]/60 p-8 flex flex-col items-center gap-2 hover:border-[#FF00E5]/60 transition-colors">
        <Upload className="w-8 h-8 text-[#FF00E5]" />
        <div className="font-black text-lg">Upload photo</div>
        <div className="text-xs text-white/50 text-center">JPG, PNG or WebP · up to 10 MB</div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])} />
      </label>
      <label data-testid="picker-camera"
        className="cursor-pointer rounded-3xl border-2 border-dashed border-[#00F0FF]/40 bg-[#150525]/60 p-8 flex flex-col items-center gap-2 hover:border-[#00F0FF]/60 transition-colors">
        <Camera className="w-8 h-8 text-[#00F0FF]" />
        <div className="font-black text-lg">Take a selfie</div>
        <div className="text-xs text-white/50 text-center">Uses your phone's front camera.</div>
        <input ref={captureRef} type="file" accept="image/*" capture="user" className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])} />
      </label>
    </div>
  );
}

function MagicSpinner({ elapsed = 0 }) {
  return (
    <div className="text-center" data-testid="magic-spinner">
      <div className="mx-auto w-16 h-16 rounded-full border-4 border-[#FF00E5]/30 border-t-[#FF00E5] animate-spin" />
      <div className="mt-4 text-sm font-bold text-white">Working the magic…</div>
      <div className="text-[11px] text-white/60 mt-0.5" data-testid="magic-spinner-elapsed">
        {elapsed > 0 ? `${elapsed}s · usually 10–60s` : "This takes 10–60 seconds."}
      </div>
    </div>
  );
}

function ResultView({ gen, srcDataUrl, onStartOver, result }) {
  const [split, setSplit] = useState(50);
  const [isPublic, setIsPublic] = useState(!!gen.is_public);
  const [showBoost, setShowBoost] = useState(false);
  const beforeUrl = srcDataUrl;
  const afterUrl = magicFileUrl(gen.result_path);

  const download = async () => {
    try {
      const r = await fetch(afterUrl, { credentials: "include" });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `jixxeo-magic-${gen.style_slug}-${gen.id.slice(0, 8)}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
  };
  const share = async () => {
    const shareUrl = `${window.location.origin}/magic/gallery`;
    try {
      const r = await fetch(afterUrl, { credentials: "include" });
      const blob = await r.blob();
      const file = new File([blob], `jixxeo-magic.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: "Made with Jixxeo Magic", text: `Check out my ${gen.style_name}!`, files: [file], url: shareUrl });
        return;
      }
    } catch (e) { if (e?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Sharing not supported"); }
  };
  const togglePublic = async () => {
    const next = !isPublic;
    setIsPublic(next);
    try { await magicToggleShare(gen.id, next); toast.success(next ? "Now shareable" : "Now private"); }
    catch { setIsPublic(!next); toast.error("Couldn't update"); }
  };

  return (
    <section className="space-y-4" data-testid="magic-result">
      <BeforeAfter beforeUrl={beforeUrl} afterUrl={afterUrl} split={split} onSplit={setSplit} />

      <div className="flex flex-wrap gap-2">
        <button data-testid="result-share-btn" onClick={share}
          className="inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full shadow-[0_0_16px_rgba(255,0,229,0.4)]">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button data-testid="result-download-btn" onClick={download}
          className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
          <Download className="w-3.5 h-3.5" /> Download
        </button>
        <button data-testid="result-toggle-public" onClick={togglePublic}
          className={"inline-flex items-center gap-1 font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full border transition-colors " +
            (isPublic ? "border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]" : "border-white/20 bg-white/5 text-white/70")}>
          {isPublic ? <><Globe className="w-3.5 h-3.5" /> Public</> : <><Lock className="w-3.5 h-3.5" /> Private</>}
        </button>
        <button data-testid="result-again-btn" onClick={onStartOver}
          className="ml-auto inline-flex items-center gap-1 border border-[#00F0FF]/30 bg-[#00F0FF]/10 text-[#00F0FF] font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
          <RotateCcw className="w-3.5 h-3.5" /> New photo
        </button>
      </div>

      {/* Boost prompt — shows when they've just made something public */}
      {isPublic && (
        <div data-testid="result-boost-hint"
          className="rounded-2xl border border-[#FFD700]/40 bg-gradient-to-r from-[#FFD700]/15 via-[#FF7A00]/10 to-transparent p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#0A0512] flex items-center justify-center shrink-0">
            <Rocket className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-sm">Want everyone to see this?</div>
            <div className="text-[11px] text-white/60">Pin your profile to the top of the community feed for 24h.</div>
          </div>
          <button data-testid="result-boost-btn" onClick={() => setShowBoost(true)}
            className="shrink-0 inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700] to-[#FF7A00] text-black font-black text-xs uppercase tracking-widest px-3 py-2 rounded-full">
            <Rocket className="w-3 h-3" /> Boost
          </button>
        </div>
      )}

      {result?.bonus_coins > 0 && (
        <div className="rounded-2xl border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] p-3 text-sm flex items-center gap-2" data-testid="bonus-notice">
          <Trophy className="w-4 h-4" fill="currentColor" /> +{result.bonus_coins} coins — Daily Challenge complete!
        </div>
      )}
      <div className="text-center text-[11px] text-white/40">
        <Coins className="w-3 h-3 inline mr-1" fill="currentColor" />
        {result?.was_free ? "Free generation used" : `${result?.coins_spent} coins spent`} · {result?.coins?.toLocaleString?.() || 0} coins remaining
      </div>

      {showBoost && <BoostModal onClose={() => setShowBoost(false)} onBoosted={() => setShowBoost(false)} />}
    </section>
  );
}

function BeforeAfter({ beforeUrl, afterUrl, split, onSplit }) {
  const rootRef = useRef(null);
  const drag = (e) => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.touches?.[0]?.clientX ?? e.clientX) - rect.left) / rect.width;
    onSplit(Math.max(0, Math.min(100, x * 100)));
  };
  return (
    <div data-testid="before-after"
      className="relative rounded-3xl overflow-hidden border border-white/10 bg-black select-none touch-none"
      onMouseMove={(e) => e.buttons === 1 && drag(e)}
      onTouchMove={drag}
      ref={rootRef}>
      <img src={afterUrl} alt="After" className="w-full max-h-[70vh] object-contain block" data-testid="ba-after" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${split}%` }}>
        <img src={beforeUrl} alt="Before" className="max-h-[70vh] object-contain block"
          style={{ width: `${(100 / Math.max(split, 0.01)) * 100}%`, maxWidth: "none" }} data-testid="ba-before" />
      </div>
      {/* handle */}
      <div className="absolute top-0 bottom-0" style={{ left: `calc(${split}% - 1px)` }}>
        <div className="h-full w-0.5 bg-white/90 shadow-[0_0_10px_rgba(255,255,255,0.6)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow-[0_0_18px_rgba(255,255,255,0.6)]">
          <div className="text-black text-[9px] font-black uppercase tracking-widest">Slide</div>
        </div>
      </div>
      {/* labels */}
      <div className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-widest bg-black/60 px-2 py-1 rounded-full">Before</div>
      <div className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-widest bg-[#FF00E5]/70 px-2 py-1 rounded-full">After</div>
      {/* fallback range for keyboard users */}
      <input type="range" min={0} max={100} value={split} onChange={(e) => onSplit(Number(e.target.value))}
        data-testid="ba-range"
        className="absolute inset-x-3 bottom-3 w-[calc(100%-1.5rem)] accent-[#FF00E5]" />
    </div>
  );
}
